import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse } from '@nexus/shared/types';

export interface InboxItem {
  id: string;
  channel: string;
  contactName: string | null;
  preview: string;
  direction: 'inbound' | 'outbound';
  timestamp: string;
  entityType: string;
  entityId: string;
  metadata?: Record<string, unknown>;
}

interface InboxState {
  items: InboxItem[];
  selectedId: string | null;
  channel: string;
  isLoading: boolean;
  error: string | null;
  fetch: (channel?: string) => Promise<void>;
  select: (id: string | null) => void;
  setChannel: (channel: string) => void;
  clearError: () => void;
}

function convertItem(raw: Record<string, unknown>): InboxItem {
  return {
    ...raw,
    timestamp: fromUnix(raw.timestamp as number),
  } as InboxItem;
}

export const useInboxStore = create<InboxState>((set) => ({
  items: [],
  selectedId: null,
  channel: 'all',
  isLoading: false,
  error: null,

  fetch: async (channel) => {
    set({ isLoading: true, error: null });
    try {
      const params: Record<string, string | undefined> = {};
      if (channel && channel !== 'all') {
        params.channel = channel;
      }
      const res = await api.get<ApiResponse<InboxItem[]>>(`/api/inbox/stream${toQueryString(params)}`);
      set({
        items: (res.data ?? []).map((item) => convertItem(item as unknown as Record<string, unknown>)),
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  select: (id) => set({ selectedId: id }),

  setChannel: (channel) => set({ channel }),

  clearError: () => set({ error: null }),
}));
